<template>
  <div>
    <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Create Audit </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <div class="mb-4">
              <v-text-field v-model="formData.checklist_name" :rules="[(v) => !!v || 'Checklist Name is required']"
                label="Audit Name" variant="outlined" density="compact" class="mb-4" required></v-text-field>

              <v-autocomplete v-model="formData.checklist_type" :items="checklistTypeOptions" item-title="title"
                item-value="value" :rules="[(v) => !!v || 'Checklist Type is required']" label="Audit Type"
                variant="outlined" density="compact" class="mb-4" required></v-autocomplete>

              <v-divider class="my-4"></v-divider>

              <div class="mb-4">
                <v-radio-group v-model="itemType" inline class="mb-4">
                  <v-radio label="Line Item" value="line_item" color="primary"></v-radio>
                  <v-radio label="Section" value="section" color="primary"></v-radio>
                </v-radio-group>

                <!-- Line Item Form -->
                <div v-if="itemType === 'line_item'" class="mb-4">
                  <v-text-field v-model="lineItemName" label="Line Item Name" variant="outlined" density="compact"
                    class="mb-2" placeholder="Enter line item name"></v-text-field>
                  <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="addLineItem"
                    :disabled="!lineItemName">
                    Add Line Item
                  </v-btn>
                </div>

                <!-- Section Form -->
                <div v-if="itemType === 'section'" class="mb-4">
                  <v-text-field v-model="sectionName" label="Section Name" variant="outlined" density="compact"
                    class="mb-2" placeholder="Enter section name"></v-text-field>

                  <!-- Add Items to Section (before adding section) -->
                  <div class="mb-4">
                    <v-text-field v-model="tempSectionItemName" label="Item Name" variant="outlined" density="compact"
                      class="mb-2" placeholder="Enter item name"></v-text-field>
                    <v-btn color="primary" variant="outlined" size="small" prepend-icon="mdi-plus"
                      @click="addTempSectionItem" :disabled="!tempSectionItemName">
                      Add Item
                    </v-btn>
                  </div>

                  <!-- Display temporary section items -->
                  <div v-if="tempSectionItems.length > 0" class="mb-4">
                    <h4 class="text-subtitle-2 mb-2">Section Items:</h4>
                    <v-chip v-for="(item, itemIndex) in tempSectionItems" :key="`temp-item-${itemIndex}`" class="ma-1"
                      closable @click:close="removeTempSectionItem(itemIndex)">
                      {{ item.name }}
                    </v-chip>
                  </div>

                  <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="addSection"
                    :disabled="!sectionName || tempSectionItems.length === 0">
                    Add Section
                  </v-btn>
                </div>
              </div>

              <draggable v-model="checklistItems" :item-key="(item) => item.id || item.name || item.section_name"
                handle=".drag-handle" animation="200">
                <template #item="{ element, index }">

                  <div>

                    <!-- ================= LINE ITEM ================= -->

                    <v-card v-if="element.type === 'line_item'" class="mb-2" variant="outlined">
                      <v-card-text class="d-flex justify-space-between align-center">

                        <div class="d-flex align-center">

                          <v-icon class="mr-2 drag-handle">mdi-drag</v-icon>

                          <!-- EDIT MODE -->
                          <v-text-field v-if="editingLineItemIndex === index" v-model="editingLineItemName"
                            density="compact" variant="outlined" style="width:220px" autofocus
                            @keyup.enter="saveLineItemEdit(index)" @keyup.esc="cancelLineItemEdit" />

                          <!-- DISPLAY MODE -->
                          <span v-else>
                            {{ element.name }}
                          </span>

                        </div>

                        <div>

                          <!-- EDIT -->
                          <v-btn icon size="small" variant="text" color="primary" @click="editLineItem(index)">
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>

                          <!-- DELETE -->
                          <v-btn icon size="small" variant="text" color="error" @click="checklistItems.splice(index, 1)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>

                        </div>

                      </v-card-text>
                    </v-card>



                    <!-- ================= SECTION ================= -->

                    <v-card v-else class="mb-4" variant="outlined">

                      <v-card-title class="d-flex justify-space-between align-center">

                        <div class="d-flex align-center">

                          <v-icon class="mr-2 drag-handle">mdi-drag</v-icon>

                          <!-- EDIT MODE -->
                          <v-text-field v-if="editingSectionIndex === index" v-model="editingSectionName"
                            density="compact" variant="outlined" style="width:220px" autofocus
                            @keyup.enter="saveSectionEdit(index)" @keyup.esc="cancelSectionEdit" />

                          <!-- DISPLAY MODE -->
                          <span v-else>
                            {{ element.section_name }}
                          </span>

                        </div>

                        <div>

                          <!-- EDIT -->
                          <v-btn icon size="small" variant="text" color="primary" @click="editSection(index)">
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>

                          <!-- DELETE -->
                          <v-btn icon size="small" variant="text" color="error" @click="checklistItems.splice(index, 1)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>

                        </div>

                      </v-card-title>


                      <!-- SECTION ITEMS -->

                      <v-card-text>

                        <div>

                          <!-- ADD NEW ITEM -->

                          <div class="d-flex align-center mb-3">

                            <v-text-field v-model="sectionItemInputs[index]" density="compact" variant="outlined"
                              placeholder="Add item" style="max-width:250px" @keyup.enter="addItemToSection(index)" />

                            <v-btn icon color="primary" variant="text" @click="addItemToSection(index)">
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>

                          </div>


                          <!-- EXISTING ITEMS -->

                          <div v-for="(item, itemIndex) in element.items" :key="itemIndex"
                            class="d-flex align-center mb-2">

                            <!-- EDIT MODE -->

                            <v-text-field v-if="editingItemSectionIndex === index && editingItemIndex === itemIndex"
                              v-model="editingItemName" density="compact" variant="outlined" style="max-width:220px"
                              @keyup.enter="saveSectionItemEdit(index, itemIndex)" @keyup.esc="cancelSectionItemEdit" />


                            <!-- DISPLAY MODE -->

                            <v-chip v-else class="ma-1">
                              {{ item.name }}
                            </v-chip>


                            <!-- EDIT -->

                            <v-btn icon size="small" variant="text" color="primary"
                              @click="editSectionItem(index, itemIndex)">
                              <v-icon size="small">mdi-pencil</v-icon>
                            </v-btn>


                            <!-- DELETE -->

                            <v-btn icon size="small" variant="text" color="error"
                              @click="removeSectionItem(index, itemIndex)">
                              <v-icon size="small">mdi-delete</v-icon>
                            </v-btn>

                          </div>

                        </div>
                      </v-card-text>

                    </v-card>

                  </div>

                </template>
              </draggable>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn color="primary" variant="flat" :disabled="!valid || loading || !checklistItems.length"
            :loading="loading" @click="handleSubmit">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { create_master_checklists } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

export default {
  name: "CreateChecklistDialog",
  components: {
    SnackbarComponent,
    draggable,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      valid: false,
      loading: false,
      formData: {
        checklist_name: "",
        checklist_type: "",
        checklist_source: "BMS",
      },
      itemType: "line_item",
      lineItemName: "",
      sectionItemInputs: {},
      editingItemSectionIndex: null,
      editingItemIndex: null,
      editingItemName: "",
      checklistItems: [],
      sectionName: "",

      tempSectionItemName: "",
      tempSectionItems: [],
      editingLineItemIndex: null,
      editingLineItemName: "",
      editingSectionIndex: null,
      editingSectionName: "",



      checklistTypeOptions: [
        { title: "Med Pass Audit", value: "MED_PASS_AUDIT" },
        { title: "Storage Audit", value: "STORAGE_AUDIT" },
      ],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
  },
  methods: {
 addItemToSection(sectionIndex) {

  const name = this.sectionItemInputs[sectionIndex];

  if (!name || !name.trim()) return;

  const section = this.checklistItems[sectionIndex];

  if (!section || section.type !== "section") return;

  if (!section.items) {
    section.items = [];
  }

  const weightage = section.items.length;

  section.items.push({
    name: name.trim(),
    weightage
  });

  this.sectionItemInputs[sectionIndex] = "";
},
editSectionItem(sectionIndex, itemIndex) {

  // save previously edited item safely
  if (
    this.editingItemSectionIndex !== null &&
    this.editingItemIndex !== null
  ) {

    const prevSection = this.editingItemSectionIndex;
    const prevItem = this.editingItemIndex;

    const section = this.checklistItems[prevSection];

    if (
      section &&
      section.type === "section" &&
      section.items &&
      section.items[prevItem]
    ) {
      if (this.editingItemName.trim()) {
        section.items[prevItem].name = this.editingItemName.trim();
      }
    }
  }

  // switch editing target
  this.editingItemSectionIndex = sectionIndex;
  this.editingItemIndex = itemIndex;

  this.editingItemName =
    this.checklistItems[sectionIndex].items[itemIndex].name;
},
    saveSectionItemEdit(sectionIndex, itemIndex) {

      if (this.editingItemName.trim()) {

        this.checklistItems[sectionIndex]
          .items[itemIndex]
          .name = this.editingItemName.trim();
      }

      this.cancelSectionItemEdit();
    },
cancelSectionItemEdit() {

  const section = this.checklistItems[this.editingItemSectionIndex];

  if (
    section &&
    section.type === "section" &&
    section.items &&
    section.items[this.editingItemIndex]
  ) {
    this.editingItemName =
      section.items[this.editingItemIndex].name;
  }

  this.editingItemSectionIndex = null;
  this.editingItemIndex = null;
  this.editingItemName = "";
},
    removeSectionItem(sectionIndex, itemIndex) {

      this.checklistItems[sectionIndex].items.splice(itemIndex, 1);

      // reindex weightage
      this.checklistItems[sectionIndex].items.forEach((item, i) => {
        item.weightage = i;
      });
    },
    editLineItem(index) {

      // if another line item was being edited → save it
      if (this.editingLineItemIndex !== null && this.editingLineItemIndex !== index) {
        const prevIndex = this.editingLineItemIndex;

        if (this.editingLineItemName.trim()) {
          this.checklistItems[prevIndex].name = this.editingLineItemName.trim();
        }
      }

      // start editing new item
      this.editingLineItemIndex = index;
      this.editingLineItemName = this.checklistItems[index].name;
    },

    saveLineItemEdit(index) {
      if (this.editingLineItemName.trim()) {
        this.checklistItems[index].name = this.editingLineItemName.trim();
      }

      this.editingLineItemIndex = null;
      this.editingLineItemName = "";
    },
    editSection(index) {

      if (this.editingSectionIndex !== null && this.editingSectionIndex !== index) {
        const prevIndex = this.editingSectionIndex;

        if (this.editingSectionName.trim()) {
          this.checklistItems[prevIndex].section_name =
            this.editingSectionName.trim();
        }
      }

      this.editingSectionIndex = index;
      this.editingSectionName = this.checklistItems[index].section_name;
    },

    saveSectionEdit(index) {
      if (this.editingSectionName.trim()) {
        this.checklistItems[index].section_name =
          this.editingSectionName.trim();
      }

      this.editingSectionIndex = null;
      this.editingSectionName = "";
    },
    addLineItem() {
      if (this.lineItemName.trim()) {
        this.checklistItems.push({
          id: Date.now() + Math.random(),
          type: "line_item",
          name: this.lineItemName.trim(),
        });
        this.lineItemName = "";
      }
    },

    cancelLineItemEdit() {
      if (this.editingLineItemIndex !== null) {
        this.editingLineItemName =
          this.checklistItems[this.editingLineItemIndex].name;
      }

      this.editingLineItemIndex = null;
    },

    addTempSectionItem() {
      if (this.tempSectionItemName.trim()) {
        const itemWeightage = this.tempSectionItems.length;
        this.tempSectionItems.push({
          name: this.tempSectionItemName.trim(),
          weightage: itemWeightage,
        });
        this.tempSectionItemName = "";
      }
    },
    removeTempSectionItem(index) {
      this.tempSectionItems.splice(index, 1);
      // Re-index weightages after removal
      this.tempSectionItems.forEach((item, idx) => {
        item.weightage = idx;
      });
    },
    addSection() {
      if (this.sectionName.trim() && this.tempSectionItems.length > 0) {
        this.checklistItems.push({
          id: Date.now() + Math.random(),
          type: "section",
          section_name: this.sectionName.trim(),
          items: [...this.tempSectionItems],
        });

        this.sectionName = "";
        this.tempSectionItemName = "";
        this.tempSectionItems = [];
      }
    },

    cancelSectionEdit() {
      this.editingSectionIndex = null;
      this.editingSectionName = "";
    },


    buildChecklistSections() {
      return this.checklistItems.map((item) => {

        if (item.type === "line_item") {
          return {
            type: "line_item",
            name: item.name
          };
        }

        if (item.type === "section") {
          return {
            type: "section",
            section_name: item.section_name,
            items: item.items.map((i, idx) => ({
              name: i.name,
              weightage: idx
            }))
          };
        }

      });
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
   resetForm() {
  this.formData = {
    checklist_name: "",
    checklist_type: "",
    checklist_source: "BMS",
  };

  this.itemType = "line_item";
  this.lineItemName = "";

  this.sectionName = "";
  this.checklistItems = [];
  this.tempSectionItemName = "";
  this.tempSectionItems = [];

  this.editingLineItemIndex = null;
  this.editingLineItemName = "";

  this.editingSectionIndex = null;
  this.editingSectionName = "";

  // ⭐ ADD THESE
  this.editingItemSectionIndex = null;
  this.editingItemIndex = null;
  this.editingItemName = "";
  this.sectionItemInputs = {};

  if (this.$refs.form) {
    this.$refs.form.resetValidation();
  }
},
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (!this.checklistItems.length) {
        this.snackbar = {
          show: true,
          message: "Please add at least one line item or section",
          color: "warning",
        };
        return;
      }

      this.loading = true;

      try {
        const checklistSections = this.buildChecklistSections();

        // Ensure it's an array
        const sectionsArray = Array.isArray(checklistSections)
          ? checklistSections
          : Object.values(checklistSections);

        // Add sort_weightage to each object (0, 1, 2, 3...)
        const sectionsWithWeightage = sectionsArray.map((item, index) => ({
          ...item,
          sort_weightage: index,
        }));

        const input = {
          checklist_name: this.formData.checklist_name,
          checklist_type: this.formData.checklist_type,
          checklist_source: this.formData.checklist_source,
          checklist_sections: JSON.stringify(sectionsWithWeightage),
        };

        const response = await client.graphql({
          query: create_master_checklists,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.create_master_checklists);

        if (responseData.status == "SUCCESS") {
          this.loading = false;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Checklist created successfully",
            color: "success",
          };
          this.$emit("created", 0);
          this.handleClose();
        } else {
          this.loading = false;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to create checklist",
            color: "error",
          };
        }
      } catch (error) {
        this.loading = false;
        console.error("Error creating checklist:", error);
        this.snackbar = {
          show: true,
          message:
            error.message || "An error occurred while creating the checklist",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
.drag-handle {
  cursor: grab;
}
</style>
